import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, MapPin, Clock, User, Tag, Sparkles, Check, MessageSquare, Pencil, Mic, MicOff, Send, Loader2, ThumbsUp, CheckCircle2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";
import PhotoAnnotator from "@/components/PhotoAnnotator";

const token = () => localStorage.getItem("sc_token");
const fileUrl = (id, variant) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${id}/file?auth=${token()}${variant ? `&variant=${variant}` : ""}`;
const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function Feed() {
  const qc = useQueryClient();
  const [tag, setTag] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["feed", tag], queryFn: async () => (await api.get(`/feed${tag ? `?tag=${encodeURIComponent(tag)}` : ""}`)).data });

  if (isLoading) return <div className="p-10"><Spinner /></div>;
  const items = data?.items || [];

  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="feed-page">
      <Eyebrow>Live from the field</Eyebrow>
      <h1 className="mt-2 font-display text-3xl font-bold text-white">Project feed</h1>
      <p className="mt-2 text-sm text-slate-400">Every job photo, time &amp; location stamped, organized by project. No more chasing crews for updates.</p>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="feed-tags">
        <button onClick={() => setTag("")} className={`rounded-full px-3 py-1 text-xs ${!tag ? "bg-amber-500 text-black" : "bg-slate-800 text-slate-300"}`}>All</button>
        {(data?.tags || []).map((t) => (
          <button key={t} data-testid={`feed-tag-${t}`} onClick={() => setTag(t)} className={`rounded-full px-3 py-1 text-xs ${tag === t ? "bg-amber-500 text-black" : "bg-slate-800 text-slate-300"}`}>#{t}</button>
        ))}
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => <PhotoCard key={m.id} m={m} onChange={() => qc.invalidateQueries({ queryKey: ["feed"] })} />)}
        {!items.length && <div className="card-tac col-span-full py-16 text-center text-slate-500" data-testid="feed-empty">No photos yet. Snap job photos from any project and they'll stream in here.</div>}
      </div>
    </div>
  );
}

function PhotoCard({ m, onChange }) {
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const addTag = async () => {
    const next = Array.from(new Set([...(m.tags || []), tagInput.trim().toLowerCase()])).filter(Boolean);
    if (!tagInput.trim()) return;
    try { await api.patch(`/media/${m.id}/meta`, { tags: next }); setTagInput(""); onChange?.(); toast.success("Tagged"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const aiCaption = async () => {
    setBusy(true);
    try { await api.post(`/media/${m.id}/caption`); toast.success("AI caption added"); onChange?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="card-tac overflow-hidden" data-testid={`feed-item-${m.id}`}>
      <div className="relative">
        <img src={`${fileUrl(m.id, showOriginal ? "original" : null)}&v=${m.updated_at || m.created_at}`} alt={m.caption || "job photo"} className="h-48 w-full object-cover" />
        {m.annotated && <span className="absolute left-2 top-2 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-black" data-testid={`annotated-badge-${m.id}`}>{showOriginal ? "Original" : "Annotated"}</span>}
        <button data-testid={`annotate-btn-${m.id}`} onClick={() => setAnnotating(true)}
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white hover:bg-black/80">
          <Pencil className="h-3 w-3" /> Annotate
        </button>
        {m.annotated && <button data-testid={`beforeafter-${m.id}`} onClick={() => setShowOriginal((s) => !s)}
          className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white hover:bg-black/80">
          {showOriginal ? "Show annotated" : "Show original"}
        </button>}
      </div>
      <div className="p-3">
        {m.caption ? <p className="text-sm text-white" data-testid={`feed-caption-${m.id}`}>{m.caption}</p>
          : <button data-testid={`feed-aicaption-${m.id}`} onClick={aiCaption} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50"><Sparkles className="h-3.5 w-3.5" /> {busy ? "Captioning…" : "AI caption"}</button>}
        <div className="mt-2 flex flex-wrap gap-1">
          {(m.tags || []).map((t) => <span key={t} className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300"><Tag className="h-2.5 w-2.5" />{t}</span>)}
        </div>
        <div className="mt-2 flex items-center gap-1">
          <input data-testid={`feed-tag-input-${m.id}`} value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="add tag…" className="w-24 rounded border border-slate-700 bg-[#0b0f17] px-2 py-0.5 text-[11px] text-white" />
          <button data-testid={`feed-tag-add-${m.id}`} onClick={addTag} className="rounded border border-slate-700 px-1.5 py-0.5 text-slate-400 hover:border-amber-500"><Check className="h-3 w-3" /></button>
        </div>
        <div className="mt-2 space-y-0.5 font-mono-tac text-[10px] text-slate-500">
          {m.job_title && <Link to={m.job_id ? `/jobs/${m.job_id}` : "#"} className="block truncate text-cyan-400 hover:underline">{m.job_title}</Link>}
          <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {when(m.created_at)}</div>
          <div className="flex items-center gap-1"><User className="h-3 w-3" /> {m.taker_name}</div>
          {(m.lat != null) && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {Number(m.lat).toFixed(4)}, {Number(m.lng).toFixed(4)}</div>}
        </div>
        <button data-testid={`comments-toggle-${m.id}`} onClick={() => setShowComments((s) => !s)}
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-cyan-300">
          <MessageSquare className="h-3.5 w-3.5" /> {showComments ? "Hide comments" : "Comments"}
        </button>
        {showComments && <Comments mediaId={m.id} />}
      </div>
      {annotating && <PhotoAnnotator mediaId={m.id} onClose={() => setAnnotating(false)} onSaved={() => onChange?.()} />}
    </div>
  );
}

function Comments({ mediaId }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const recorderRef = useRef(null);

  const load = () => api.get(`/media/${mediaId}/comments`).then((r) => setData(r.data)).catch(() => setData({ comments: [], resolved: false }));
  useEffect(() => { load(); /* eslint-disable-line */ }, [mediaId]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/media/${mediaId}/comments`, { text: text.trim() });
      setText(""); await load();
      toast.success(res.notified ? `Comment posted · ${res.notified} mentioned` : "Comment posted");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const react = async (cid, emoji) => {
    try { await api.post(`/media/${mediaId}/comments/${cid}/react`, { emoji }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleResolve = async () => {
    try {
      const { data: res } = await api.post(`/media/${mediaId}/comments/resolve`, { resolved: !data?.resolved });
      setData((x) => ({ ...x, resolved: res.resolved }));
      toast.success(res.resolved ? "Thread resolved" : "Thread reopened");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const startVoice = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = "en-US";
      r.onresult = (e) => { let f = ""; for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) f += e.results[i][0].transcript + " "; if (f) setText((n) => (n ? n + " " : "") + f.trim()); };
      r.onend = () => setListening(false); r.onerror = () => setListening(false);
      recogRef.current = r; r.start(); setListening(true); return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = []; const rec = new MediaRecorder(s);
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        s.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const fd = new FormData(); fd.append("file", blob, "note.webm");
        try { const { data: tr } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (tr.text) setText((n) => (n ? n + " " : "") + tr.text.trim()); }
        catch { toast.error("Transcription failed"); }
        setListening(false);
      };
      recorderRef.current = rec; rec.start(); setListening(true);
    } catch { toast.error("Microphone unavailable"); }
  };
  const stopVoice = () => {
    try { recogRef.current?.stop(); } catch { /* */ }
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* */ }
    setListening(false);
  };

  const rows = data?.comments;
  const resolved = data?.resolved;
  const rc = (c, e) => (c.reactions?.[e] || []).length;

  return (
    <div className="mt-2 rounded-lg border border-slate-800 bg-[#0b0f17] p-2" data-testid={`comments-${mediaId}`}>
      <div className="mb-2 flex items-center justify-between">
        {resolved
          ? <span data-testid={`resolved-badge-${mediaId}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Resolved</span>
          : <span className="font-mono-tac text-[10px] uppercase text-slate-500">Discussion</span>}
        <button data-testid={`resolve-toggle-${mediaId}`} onClick={toggleResolve} className={`rounded-full border px-2 py-0.5 text-[10px] ${resolved ? "border-slate-700 text-slate-400" : "border-emerald-600/50 text-emerald-300 hover:bg-emerald-500/10"}`}>
          {resolved ? "Reopen" : "Resolve"}
        </button>
      </div>
      <div className="mb-2 max-h-44 space-y-2 overflow-y-auto">
        {rows == null ? <div className="py-2 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-600" /></div>
          : rows.length ? rows.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="text-[11px]">
              <span className="font-semibold text-slate-200">{c.author_name}</span>
              <span className="ml-1 text-slate-300">{c.text}</span>
              {!!(c.mentions || []).length && <span className="ml-1 text-cyan-400">· {c.mentions.length} mentioned</span>}
              <div className="mt-0.5 flex items-center gap-2">
                <button data-testid={`react-up-${c.id}`} onClick={() => react(c.id, "👍")} className="inline-flex items-center gap-0.5 text-slate-500 hover:text-amber-300"><ThumbsUp className="h-3 w-3" />{rc(c, "👍") || ""}</button>
                <button data-testid={`react-done-${c.id}`} onClick={() => react(c.id, "✅")} className="inline-flex items-center gap-0.5 text-slate-500 hover:text-emerald-300"><CheckCircle2 className="h-3 w-3" />{rc(c, "✅") || ""}</button>
              </div>
            </div>
          )) : <p className="py-1 text-[11px] text-slate-600">No comments yet. Use @name to notify a teammate.</p>}
      </div>
      <div className="flex items-center gap-1.5">
        <input data-testid={`comment-input-${mediaId}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Comment… @name to notify"
          className="min-w-0 flex-1 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-[11px] text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none" />
        <button data-testid={`comment-voice-${mediaId}`} onClick={listening ? stopVoice : startVoice} className={`rounded border px-1.5 py-1 ${listening ? "border-rose-500 text-rose-300" : "border-slate-700 text-slate-400"}`}>{listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}</button>
        <button data-testid={`comment-send-${mediaId}`} onClick={send} disabled={busy} className="rounded border border-slate-700 px-1.5 py-1 text-slate-300 hover:border-amber-500 disabled:opacity-50"><Send className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}
